import { Divider } from '@mui/material';
import {
  IconSun,
  IconMinusVertical,
  IconMoon,
  IconScubaMask,
  IconUser,
  IconExclamationCircle,
  IconBed,
} from '@tabler/icons-react';
import { formatDateRange } from '../../../common/utilities/dateFormatter';
import { formatPrice } from '../../../common/utilities/priceFormatter';
import { groupRoomsAndCalculatePrice } from '../utils/roomUtils';
import { getRoomOriginalImageUrl } from '../../../common/utilities/cdnImage';
import CdnImage from '../../../components/CdnImage';
import type {
  AvailabilityDetail,
  BoatQuery,
  RoomTypesQuery,
} from '../../../gql/graphql';
import type { RoomDetail } from '../../../hooks/useCreateBooking';

interface BoatActionProps {
  selectedDetail: AvailabilityDetail | null;
  totalPrice: number;
  boat: BoatQuery | undefined;
  selectedRoomIds: RoomDetail[];
  boatRooms: RoomTypesQuery | undefined;
  divers: number;
  currentSettedDivers: number;
}

const BoatAction = ({
  selectedDetail,
  totalPrice,
  boat,
  selectedRoomIds,
  boatRooms,
  divers,
  currentSettedDivers,
}: BoatActionProps) => {
  if (selectedDetail && totalPrice > 0) {
    return (
      <div className="flex flex-col items-start gap-6 w-full">
        <span className="font-semibold text-2xl pt-6">{boat?.boat.name}</span>
        <div className="flex items-center gap-4 self-stretch">
          <div className="flex flex-col items-start gap-2 w-36">
            <span className="text-xl font-semibold text-neutral-800">
              {formatDateRange(
                selectedDetail.embarkationDate ?? '',
                selectedDetail?.disEmbarkationDate ?? '',
              )}
            </span>
            <div className="flex item-start gap-2 text-neutral-500">
              <div className="flex items-center gap-1">
                <IconSun size={20} />
                <span>{selectedDetail?.numberOfDay}D</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-200">
                <IconMinusVertical size={24} />
              </div>
              <div className="flex items-center gap-1">
                <IconMoon size={20} />
                <span>{selectedDetail?.numberOfNight}N</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2">
            <span className="text-xl font-semibold text-neutral-800">
              {selectedDetail?.routeName}
            </span>
            <div className="flex items-center gap-1 text-neutral-500">
              <IconScubaMask size={20} />
              <span>{selectedDetail?.numberOfDive} dives</span>
            </div>
          </div>
        </div>
        <div className="flex p-4 flex-col items-start gap-4 self-stretch bg-neutral-20">
          {groupRoomsAndCalculatePrice(selectedRoomIds, boatRooms).map(
            ({ typeId, totalPrice }) => {
              const boatRoom = boatRooms?.roomTypes.find(
                room => room.typeId === typeId,
              );
              return (
                <div
                  key={typeId}
                  className="flex justify-between items-center self-stretch"
                >
                  <div className="flex items-center gap-4">
                    <CdnImage
                      className="h-20 w-20 shrink-0 object-cover"
                      src={getRoomOriginalImageUrl(boatRoom?.roomPhotos)}
                      variant="ROOMGRID"
                      alt={`room ${boatRoom?.typeName}`}
                    />
                    <div className="flex flex-col items-start gap-2">
                      <span className="font-semibold">
                        {boatRoom?.typeName}
                      </span>
                      <div className="flex items-center gap-1">
                        <IconUser size={16} />
                        <span>{boatRoom?.occupancy} guest</span>
                      </div>
                      <span>
                        {formatPrice(boatRoom?.price ?? 0, {
                          currencySymbol: boat?.boat.currency,
                        })}{' '}
                        per guest
                      </span>
                    </div>
                  </div>
                  <span>
                    {formatPrice(totalPrice, {
                      currencySymbol: boat?.boat.currency,
                    })}
                  </span>
                </div>
              );
            },
          )}
          {/* <div className="w-full">
                <Divider className="bg-neutral-100" />
              </div>
              <div className="flex justify-between items-center self-stretch">
                <span>Santtai service fee</span>
                <span>{formatPrice(calculateServiceFee(totalPrice))}</span>
              </div>
              <div className="flex justify-between items-center self-stretch">
                <span>Taxes</span>
                <span>
                  {formatPrice(calculateTaxes(calculateServiceFee(totalPrice)))}
                </span>
              </div> */}
          <div className="w-full">
            <Divider className="bg-neutral-100" />
          </div>
          <div className="flex justify-between items-center self-stretch text-xl font-semibold">
            <span>Total</span>
            <span>
              {formatPrice(totalPrice, {
                currencySymbol: boat?.boat.currency,
              })}
            </span>
          </div>
          <div className="w-full">
            <Divider className="bg-neutral-100" />
          </div>
          <div className="text-green-700">
            <span className="font-semibold">Book now, pay later.</span>
            <span className="ml-2">
              We'll hold your spot, free of charge. No credit card needed!
            </span>
          </div>
        </div>
        {divers > currentSettedDivers && (
          <div className="flex items-start gap-4 self-stretch">
            <IconExclamationCircle size={20} />
            <span>
              There remains{' '}
              <b>
                {divers - currentSettedDivers} guests without the allocated room
              </b>
            </span>
          </div>
        )}
      </div>
    );
  }

  if (selectedDetail !== null) {
    return (
      <div className="flex p-6 flex-col items-start gap-6 w-full">
        <span className="font-semibold text-2xl">{boat?.boat.name}</span>
        <div className="flex items-start gap-4">
          <div className="flex flex-col gap-2 w-[9rem]">
            <span className="text-xl text-neutral-800 font-semibold">
              {formatDateRange(
                selectedDetail.embarkationDate,
                selectedDetail.disEmbarkationDate,
              )}
            </span>
            <div className="flex items-start gap-2 text-neutral-500">
              <div className="flex items-center gap-1">
                <IconSun size={20} />
                <span className="font-normal">
                  {selectedDetail?.numberOfDay}D
                </span>
              </div>
              <div className="flex justify-center items-center gap-2 text-neutral-100">
                <IconMinusVertical size={20} />
              </div>
              <div className="flex items-center gap-1">
                <IconMoon size={20} />
                <span className="font-normal">
                  {selectedDetail?.numberOfNight}N
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2">
            <span className="text-xl font-semibold">
              {selectedDetail.routeName}
            </span>
            <div className="flex items-center gap-1 text-neutral-500">
              <IconScubaMask size={20} />
              <span>{selectedDetail.numberOfDive} dives</span>
            </div>
          </div>
        </div>
        <div className="flex p-4 items-center gap-4 self-stretch bg-neutral-20">
          <IconBed size={24} />
          <div className="flex flex-col items-start gap-2">
            <span className="font-semibold text-neutral-800">
              No selected room
            </span>
            <span className="text-neutral-500">
              Please select your rooms to stay on the trip
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex p-4 items-center gap-4 self-stretch bg-neutral-20">
      <IconScubaMask size={24} />
      <div className="flex flex-col items-start gap-2">
        <span className="font-semibold text-neutral-800">No selected trip</span>
        <span className="text-neutral-500">Please select your diving trip</span>
      </div>
    </div>
  );
};

export default BoatAction;
